import { Box, Stack } from '../primitives';

export interface PeriodCellProps {
  /** The start, already formatted for the reader's language; null when there is none. */
  start: string | null;
  /** The end, already formatted; null means the period has no end date. */
  end: string | null;
  /** Shown in place of a missing start, e.g. "Starts on approval". */
  noStartLabel: string;
  /** Shown in place of a missing end, e.g. "No end date". */
  noEndLabel: string;
}

/**
 * A period — a contract's, an appointment's, a seat's — in one line.
 *
 * Replaces two copies of a term cell that printed an `OE` abbreviation for "no
 * end date" and an em dash for "no start". Both read as noise to the people
 * this portal is for: a clerk does not know what `OE` stands for, and `— →`
 * says nothing at all. The words are spelled out instead, in the secondary
 * colour, so the real dates keep the visual weight.
 *
 * Dates arrive formatted because this library has no i18n dependency by
 * design: each app formats in its reader's language and passes the labels.
 */
export function PeriodCell({ start, end, noStartLabel, noEndLabel }: PeriodCellProps) {
  const missing = { color: 'text.secondary' } as const;
  return (
    <Stack
      direction="row"
      spacing={0.75}
      sx={{ alignItems: 'center', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
    >
      <Box component="span" sx={start ? undefined : missing}>
        {start ?? noStartLabel}
      </Box>
      <Box component="span" sx={{ color: 'text.disabled' }} aria-hidden>
        –
      </Box>
      <Box component="span" sx={end ? undefined : missing}>
        {end ?? noEndLabel}
      </Box>
    </Stack>
  );
}
